import React from 'react';
import {
  BedsIcon,
  TableIcon,
  SpaIcon,
  TreatmentIcon,
  FitnessCentreIcon,
  FacilityIcon,
  ShoppingCartIcon,
  GiftIcon,
  TicketIcon,
  TickCircleIcon,
} from '@root/styles/icons';
import { Property } from 'shared-types/Property';
import { EntityTypes } from '@common-types/Entities';
import { useTranslations } from '@lang';
import { Link } from 'shared-routing';
import { Routes } from '@common/constants';
import {
  Wrapper,
  Inner,
  Text,
  BgImage,
  BgImageWrapper,
  ReadyToCheckout,
} from './AddAnother.styles';
import { useModuleStatuses } from '@features/properties';
import { useLabels } from '../Menu/useLabels';

const iconMap = {
  [EntityTypes.ROOM]: <BedsIcon />,
  [EntityTypes.TABLE]: <TableIcon />,
  [EntityTypes.SPA_DAY]: <SpaIcon />,
  [EntityTypes.TREATMENT]: <TreatmentIcon />,
  [EntityTypes.SPA_CLASS]: <FitnessCentreIcon />,
  [EntityTypes.FACILITY]: <FacilityIcon />,
  [EntityTypes.PRODUCT]: <ShoppingCartIcon />,
  [EntityTypes.VOUCHER]: <GiftIcon />,
  [EntityTypes.EVENT]: <TicketIcon />,
  [EntityTypes.SPA_EXPERIENCE]: <SpaIcon />,
};

const urlMap = {
  [EntityTypes.ROOM]: Routes.ROOM_LIST,
  [EntityTypes.TABLE]: Routes.TABLES_HOME,
  [EntityTypes.SPA_DAY]: Routes.SPA_HOME,
  [EntityTypes.TREATMENT]: Routes.SPA_TREATMENT_LIST,
  [EntityTypes.SPA_CLASS]: Routes.SPA_CLASSES_LIST,
  [EntityTypes.FACILITY]: Routes.FACILITY_LIST,
  [EntityTypes.PRODUCT]: Routes.PRODUCT_LIST,
  [EntityTypes.VOUCHER]: Routes.VOUCHER_LIST,
  [EntityTypes.EVENT]: Routes.EVENT_LIST,
  [EntityTypes.SPA_EXPERIENCE]: Routes.SPA_HOME,
};

type AddAnotherProps = {
  type: EntityTypes;
  property: Property;
  backgroundImageUrl?: string;
  isActive?: boolean;
  userHasAccessToFacilities?: boolean;
  userHasAccessToSpaClasses?: boolean;
};

export const AddAnother: React.FC<AddAnotherProps> = ({
  type,
  property,
  backgroundImageUrl,
  isActive = false,
  userHasAccessToFacilities,
  userHasAccessToSpaClasses,
}) => {
  const { t } = useTranslations();
  const labels = useLabels();

  const {
    statuses: { isRoomsEnabled },
  } = useModuleStatuses();

  const labelMap = {
    [EntityTypes.ROOM]: t('basket.addRoom', {
      roomCustomLabel: labels?.rooms?.toLowerCase() || 'a room',
    }),
    [EntityTypes.TABLE]: t('basket.addTable'),
    [EntityTypes.SPA_DAY]: t('basket.addSpaDay', {
      spaCustomLabel: labels?.spa?.toLowerCase() || 'spa',
    }),
    [EntityTypes.TREATMENT]: t('basket.addTreatment', {
      treatmentCustomLabel: labels?.treatments?.toLowerCase() || 'treatments',
    }),
    [EntityTypes.SPA_CLASS]: t('basket.addSpaClass', {
      classesCustomLabel: labels?.classes?.toLowerCase() || 'spa class & activities',
    }),
    [EntityTypes.FACILITY]: t('basket.addFacility', {
      facilityCustomLabel: labels?.facilities?.toLowerCase() || 'a facility',
    }),
    [EntityTypes.PRODUCT]: t('basket.addProduct'),
    [EntityTypes.VOUCHER]: t('basket.addVoucher', {
      vouchersCustomLabel: labels?.vouchers?.toLowerCase() || 'a voucher',
    }),
    [EntityTypes.EVENT]: t('basket.addEvent', {
      eventsCustomLabel: labels?.events?.toLowerCase() || 'an event',
    }),
    [EntityTypes.SPA_EXPERIENCE]: t('basket.addSpaDay', {
      spaCustomLabel: labels?.spa?.toLowerCase() || 'spa',
    }),
  };

  const activeLabelMap = {
    [EntityTypes.ROOM]: t('basket.addRoomActive', {
      roomCustomLabel: labels?.rooms || 'Room',
    }),
    [EntityTypes.TABLE]: t('basket.addTableActive'),
    [EntityTypes.SPA_DAY]: t('basket.addSpaActive', {
      spaCustomLabel: labels?.spa || 'Spa',
    }),
    [EntityTypes.TREATMENT]: t('basket.addSpaActive', {
      spaCustomLabel: labels?.spa || 'Spa',
    }),
    [EntityTypes.SPA_CLASS]: t('basket.addSpaActive', {
      spaCustomLabel: labels?.spa || 'Spa',
    }),
    [EntityTypes.FACILITY]: t('basket.addFacility', {
      facilityCustomLabel: labels?.facilities?.toLowerCase() || 'a facility',
    }),
    [EntityTypes.PRODUCT]: t('basket.addProduct'),
    [EntityTypes.VOUCHER]: t('basket.addVoucher', {
      vouchersCustomLabel: labels?.vouchers?.toLowerCase() || 'a voucher',
    }),
    [EntityTypes.EVENT]: t('basket.addEvent', {
      eventsCustomLabel: labels?.events?.toLowerCase() || 'an event',
    }),
    [EntityTypes.SPA_EXPERIENCE]: t('basket.addSpaActive', {
      spaCustomLabel: labels?.spa || 'Spa',
    }),
  };

  const modules = property.modulesExcludingDisabled;
  const spa = modules?.spa;

  const accessToFacilities =
    typeof userHasAccessToFacilities === 'undefined' ? false : userHasAccessToFacilities;

  const accessToSpaClasses =
    typeof userHasAccessToSpaClasses === 'undefined' ? false : userHasAccessToSpaClasses;

  const propertyCheckMap = {
    [EntityTypes.ROOM]: isRoomsEnabled,
    [EntityTypes.TABLE]: modules?.tables?.enabled,
    [EntityTypes.SPA_DAY]: spa?.enabled && spa?.features?.days?.enabled,
    [EntityTypes.TREATMENT]: spa?.enabled && spa?.features?.treatments?.enabled,
    [EntityTypes.FACILITY]: accessToFacilities && spa?.features?.facilities?.enabled,
    [EntityTypes.SPA_CLASS]: accessToSpaClasses && spa?.features?.classes?.enabled,
    [EntityTypes.PRODUCT]: modules?.products?.enabled,
    [EntityTypes.VOUCHER]: modules?.vouchers?.enabled,
    [EntityTypes.EVENT]: modules?.events?.enabled,
    [EntityTypes.SPA_EXPERIENCE]:
      spa?.enabled &&
      (spa?.features?.days?.enabled ||
        spa?.features?.treatments?.enabled ||
        (accessToFacilities && spa?.features?.facilities?.enabled) ||
        (accessToSpaClasses && spa?.features?.classes?.enabled)),
  };

  return propertyCheckMap[type] ? (
    <Wrapper>
      <Link route={urlMap[type]}>
        <Inner isActive={isActive}>
          {isActive ? <TickCircleIcon /> : iconMap[type]}
          {isActive && backgroundImageUrl && (
            <BgImageWrapper>
              <BgImage isFullDim src={backgroundImageUrl} />
            </BgImageWrapper>
          )}
        </Inner>
        <Text isActive={isActive}>
          {isActive ? activeLabelMap[type] : labelMap[type]}
          {isActive && (
            <ReadyToCheckout isActive={isActive}>{t('basket.readyToCheckout')}</ReadyToCheckout>
          )}
        </Text>
      </Link>
    </Wrapper>
  ) : null;
};
